import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Skeleton } from '@monksflow/monks-ui';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderActions,
  PageHeaderBackButton,
  PageHeaderBreadcrumb,
  PageHeaderBreadcrumbLink,
  PageHeaderBreadcrumbPage,
  PageHeaderEditableName,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';

interface AdminPageShellProps {
  /** Names the page in the header. */
  title: string;
  /**
   * The pages above this one, root first. A page reached from a list gives at
   * least the list it came from, so the header can offer the way back.
   */
  parents?: { label: string; href: string }[];
  /**
   * Opt out of the body card's inset, for a page that already pays it the way
   * every other redesigned list page does: the toolbar and the table's scroll
   * region each carry their own, so paying it here too insets them twice.
   */
  ownsBodyInset?: boolean;
  /**
   * No body card at all, for a page that lays its own cards out on the page with the page's gap
   * between them, as the chat page does.
   */
  cardless?: boolean;
  /** Makes the title renamable in place; `label` names the field for assistive tech. */
  rename?: { onCommit: (next: string) => void; label: string };
  /** The title is still loading: its place in the breadcrumb is held, so the header does not jump. */
  titlePending?: boolean;
  /** Drawn at the right of the header, such as a page's own toolbar. */
  actions?: ReactNode;
  children: ReactNode;
}

/**
 * The surface an `/admin` settings page draws itself on. The frame around the
 * page supplies nothing but an inset, so the page owns the header card and the
 * body card every other list page owns.
 */
export function AdminPageShell({
  title,
  parents = [],
  ownsBodyInset = false,
  cardless = false,
  rename,
  titlePending = false,
  actions,
  children,
}: AdminPageShellProps) {
  const navigate = useNavigate();
  const nearestAncestor = parents.at(-1);

  return (
    <ListPageLayout>
      {nearestAncestor ? (
        <PageHeader focus>
          <PageHeaderIdentity>
            <PageHeaderBackButton onClick={() => void navigate(nearestAncestor.href)} />
            <PageHeaderBreadcrumb>
              {parents.map((parent) => (
                <PageHeaderBreadcrumbLink key={parent.href} href={parent.href}>
                  {parent.label}
                </PageHeaderBreadcrumbLink>
              ))}
              {titlePending ? (
                <Skeleton data-testid="page-header-title-pending" className="h-5 w-40" />
              ) : rename ? (
                <PageHeaderEditableName
                  value={title}
                  onCommit={rename.onCommit}
                  label={rename.label}
                />
              ) : (
                <PageHeaderBreadcrumbPage>{title}</PageHeaderBreadcrumbPage>
              )}
            </PageHeaderBreadcrumb>
          </PageHeaderIdentity>
          {actions && <PageHeaderActions>{actions}</PageHeaderActions>}
        </PageHeader>
      ) : (
        <PageHeader>
          <PageHeaderIdentity>
            <PageHeaderTitle>{title}</PageHeaderTitle>
          </PageHeaderIdentity>
          {actions && <PageHeaderActions>{actions}</PageHeaderActions>}
        </PageHeader>
      )}

      {cardless ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        <ListPageBody>
          {ownsBodyInset ? (
            children
          ) : (
            <div className="min-h-0 flex-1 overflow-auto p-section">{children}</div>
          )}
        </ListPageBody>
      )}
    </ListPageLayout>
  );
}
